/**
 * Subitem id must use the parent id as prefix, except User Management
 * which stays `user-management` so existing bookmarks keep working.
 */

export const DELEGATION_MANAGEMENT_IDENTIFIER =
  'administration-delegation-management'

/**
 * Driver Settlement abilities are matched by identifier, not numeric id,
 * because the backend ability table's auto-increment numbers for these
 * abilities differ between QA and production (QA: 91-98, prod: 99-106).
 * The identifier strings below are stable across environments and were
 * confirmed against the production ability table. Note settlementCycle's
 * backend identifier is 'driver-settlement-cycle', not
 * 'driver-settlement-settlement-cycle' — it does not match this file's own
 * menu id for that item.
 */
export const DRIVER_SETTLEMENT_IDENTIFIERS = {
  root: 'driver-settlement',
  priceCards: 'driver-settlement-price-cards',
  settlementCycle: 'driver-settlement-cycle',
  reportRecipient: 'driver-settlement-report-recipient',
  sameAddressMultiOrder: 'driver-settlement-same-address-multi-order',
  insuranceDeductionWaiver: 'driver-settlement-insurance-deduction-waiver',
  blacklist: 'driver-settlement-blacklist'
} as const

export type MenuItem = {
  id: string
  title?: string
  access_ability_id?: number
  access_ability_identifier?: string
  iconName?: string
  subitems?: MenuItem[]
}

function isMenuGranted(
  item: MenuItem,
  authorizedAbilities: number[],
  authorizedIdentifiers: string[]
): boolean {
  if (item.access_ability_identifier) {
    return authorizedIdentifiers.includes(item.access_ability_identifier)
  }
  return (
    typeof item.access_ability_id === 'number' &&
    authorizedAbilities.includes(item.access_ability_id)
  )
}

export function getAuthorizedMenuItems(
  items: MenuItem[],
  authorizedAbilities: number[],
  alwaysVisibleMenuIds: string[] = [],
  authorizedIdentifiers: string[] = []
): MenuItem[] {
  return items.reduce<MenuItem[]>((accumulator, item) => {
    const parentGranted =
      isMenuGranted(item, authorizedAbilities, authorizedIdentifiers) ||
      alwaysVisibleMenuIds.includes(item.id)

    if (parentGranted) {
      accumulator.push(item)
      return accumulator
    }

    if (item.subitems && item.subitems.length > 0) {
      const filteredSubItems = getAuthorizedMenuItems(
        item.subitems,
        authorizedAbilities,
        alwaysVisibleMenuIds,
        authorizedIdentifiers
      )
      if (filteredSubItems.length > 0) {
        accumulator.push({ ...item, subitems: filteredSubItems })
      }
    }
    return accumulator
  }, [])
}

const CAMenuItems: MenuItem[] = [
  {
    id: 'user-management',
    title: 'User Management',
    access_ability_id: 1,
    iconName: 'ManageAccounts'
  },
  {
    id: 'ka-toolkits',
    title: 'KA Toolkits',
    access_ability_id: 2,
    iconName: 'Apps',
    subitems: [
      {
        id: 'ka-toolkits-sla-ext-request',
        title: 'SLA Extension Request',
        access_ability_id: 4
      },
      {
        id: 'ka-toolkits-sla-ext-hist',
        title: 'SLA Extension History',
        access_ability_id: 5
      }
    ]
  },
  {
    id: 'ca-rate-card',
    title: 'Rate Card',
    access_ability_id: 3,
    iconName: 'Cash',
    subitems: [
      {
        id: 'ca-rate-card-partner-carrier',
        access_ability_id: 6,
        title: 'Partner and Carrier'
      },
      {
        id: 'ca-rate-card-management',
        access_ability_id: 7,
        title: 'Rate Card Management',
        subitems: [
          {
            id: 'ca-rate-card-creation'
          }
        ]
      }
    ]
  },
  {
    id: 'ca-billing',
    title: 'Billing and Invoice',
    access_ability_id: 27,
    iconName: 'ReceiptLong',
    subitems: [
      {
        id: 'ca-billing-management',
        access_ability_id: 28,
        title: 'Billing Management'
      },
      {
        id: 'ca-billing-supplementary',
        access_ability_id: 29,
        title: 'Billing Supplementary'
      },
      {
        id: 'ca-billing-invoice-management',
        access_ability_id: 30,
        title: 'Invoice Management'
      },
      {
        id: 'ca-billing-invoice-number-generator',
        access_ability_id: 31,
        title: 'Invoice Number Generator'
      }
    ]
  },
  {
    id: 'order-download',
    title: 'Order Download',
    access_ability_id: 32,
    iconName: 'Download',
    subitems: [
      {
        id: 'order-download-tool',
        title: 'Order Download',
        access_ability_id: 33
      }
    ]
  }
]

export const USMenuItems: MenuItem[] = [
  {
    id: 'metrics-page',
    title: 'Metrics Overview',
    access_ability_id: 77,
    iconName: 'Analytics'
  },
  {
    id: 'administration',
    title: 'Administration',
    iconName: 'AdminPanelSettings',
    subitems: [
      {
        id: 'user-management',
        title: 'User Management',
        access_ability_id: 1
      },
      {
        id: 'administration-delegation-management',
        title: 'Delegation Management',
        access_ability_identifier: DELEGATION_MANAGEMENT_IDENTIFIER
      }
    ]
  },
  {
    id: 'merchant-management',
    title: 'Merchant Management',
    access_ability_id: 60,
    iconName: 'ContactMail'
  },
  {
    id: 'approve-task-center',
    title: 'Approve Task Center',
    access_ability_id: 53,
    iconName: 'Flaky'
  },
  {
    id: 'billing',
    title: 'Billing and Invoice',
    access_ability_id: 2,
    iconName: 'ReceiptLong',
    subitems: [
      {
        id: 'billing-quarantine-shipment',
        title: 'Quarantined Shipments',
        access_ability_id: 4
      },
      {
        id: 'billing-bef-management',
        title: 'BEF Management',
        access_ability_id: 5,
        subitems: [
          {
            id: 'billing-bef-summary'
          }
        ]
      },
      {
        id: 'billing-work-order-management',
        title: 'Work Order Management',
        access_ability_id: 6
      },
      {
        id: 'billing-payment-proof',
        title: 'Payment Proof',
        access_ability_id: 85
      }
    ]
  },
  {
    id: 'rate-card',
    title: 'Rate Card',
    access_ability_id: 7,
    iconName: 'Cash',
    subitems: [
      {
        id: 'rate-card-management',
        access_ability_id: 49,
        title: 'Rate Cards Management',
        subitems: [
          {
            id: 'rate-card-creation'
          }
        ]
      },
      {
        id: 'rate-card-commonzone-list',
        access_ability_id: 55,
        title: 'Common Zone List'
      },
      {
        id: 'rate-card-conversion',
        title: 'Zone Chart Management',
        access_ability_id: 54
      }
    ]
  },
  {
    id: 'fleet-settlement',
    title: 'DSP Settlement',
    access_ability_id: 3,
    iconName: 'Payments',
    subitems: [
      {
        id: 'fleet-settlement-payment-adjustments',
        title: 'Payment Adjustments',
        access_ability_id: 56
      },
      {
        id: 'fleet-settlement-billing',
        title: 'DSP Billing',
        access_ability_id: 57
        // Phase 1: Detail page excluded — uncomment for Phase 2
        // subitems: [
        //   {
        //     id: 'fleet-settlement-billing-detail'
        //   }
        // ]
      }
      // Phase 1: Fleet Invoice excluded — uncomment when ready
      // {
      //   id: 'fleet-settlement-invoice',
      //   title: 'DSP Invoice',
      //   access_ability_id: 58
      // }
    ]
  },
  {
    id: 'driver-settlement',
    title: 'Driver Settlement',
    access_ability_identifier: DRIVER_SETTLEMENT_IDENTIFIERS.root,
    iconName: 'LocalShipping',
    subitems: [
      {
        id: 'driver-settlement-price-cards',
        title: 'Driver Price Cards',
        access_ability_identifier: DRIVER_SETTLEMENT_IDENTIFIERS.priceCards
      },
      {
        id: 'driver-settlement-settlement-cycle',
        title: 'Settlement Cycle',
        access_ability_identifier: DRIVER_SETTLEMENT_IDENTIFIERS.settlementCycle
      },
      {
        id: 'driver-settlement-report-recipient',
        title: 'Report Recipient',
        access_ability_identifier: DRIVER_SETTLEMENT_IDENTIFIERS.reportRecipient
      },
      {
        id: 'driver-settlement-same-address-multi-order',
        title: 'Same-Address Multi-Order',
        access_ability_identifier:
          DRIVER_SETTLEMENT_IDENTIFIERS.sameAddressMultiOrder
      },
      {
        id: 'driver-settlement-insurance-deduction-waiver',
        title: 'Insurance Deduction Waiver',
        access_ability_identifier:
          DRIVER_SETTLEMENT_IDENTIFIERS.insuranceDeductionWaiver
      },
      {
        id: 'driver-settlement-blacklist',
        title: 'Blacklist',
        access_ability_identifier: DRIVER_SETTLEMENT_IDENTIFIERS.blacklist
      }
    ]
  },
  {
    id: 'order-download',
    title: 'Order Download',
    access_ability_id: 8,
    iconName: 'Download',
    subitems: [
      {
        id: 'order-download-tool',
        title: 'Order Download',
        access_ability_id: 9
      },
      {
        id: 'order-download-claim-data',
        title: 'Claim Billing Lookup',
        access_ability_id: 88
      }
    ]
  }
]

export const menuItems =
  import.meta.env.VITE_APP_COUNTRY === 'CA' ? CAMenuItems : USMenuItems
